import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Button } from './button'

/**
 * Valor copiável: um endereço, uma chave mostrada uma vez, um comando. O texto fica à vista e
 * selecionável (`select-all`), com "Copiar" ao lado; copiado, o botão diz "Copiado" por 2 segundos.
 * Quando o navegador não deixa copiar, a linha de baixo diz como copiar à mão — o valor nunca some.
 * `multiline` é para comando e trecho de código: quebra a linha como veio, em bloco de pampas.
 * Sem fonte própria: Lato no valor, como o resto do corpo (02-tokens).
 */
export interface CopyFieldProps {
  value: string
  /** Rótulo em caixa alta acima do valor ("ENDEREÇO DO MCP"). */
  label?: ReactNode
  /** Linha de apoio abaixo: o que o valor faz, por quanto tempo vale. */
  note?: ReactNode
  /** Bloco de comando ou código: a quebra de linha é a do valor. */
  multiline?: boolean
  /** O valor em Lexend, maior: a chave ou a senha que a pessoa vai conferir de olho. */
  emphasis?: boolean
  copyLabel?: string
  copiedLabel?: string
  /** O que fazer quando o navegador recusa a cópia. */
  failureMessage?: string
  /** Avisa quem usa (um toast, por exemplo) que a cópia deu certo. */
  onCopied?: () => void
  /** Nome acessível do botão, quando a tela tem mais de um ("Copiar o endereço do MCP"). */
  copyAriaLabel?: string
  className?: string
}

export function CopyField({
  value,
  label,
  note,
  multiline,
  emphasis,
  copyLabel = 'Copiar',
  copiedLabel = 'Copiado',
  failureMessage = 'O navegador não deixou copiar. Selecione o texto acima e copie com o teclado.',
  onCopied,
  copyAriaLabel,
  className,
}: CopyFieldProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setState('copied')
      onCopied?.()
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setState('idle'), 2000)
    } catch {
      setState('failed')
    }
  }

  const button = (
    <Button
      variant="outline"
      size="sm"
      className="shrink-0"
      aria-label={copyAriaLabel}
      onClick={() => void copy()}
    >
      {state === 'copied' ? copiedLabel : copyLabel}
    </Button>
  )

  return (
    <div data-slot="copy-field" className={cn('min-w-0', className)}>
      {label ? <div className="eyebrow mb-2">{label}</div> : null}
      {multiline ? (
        <div className="border-hairline-strong bg-pampas border">
          <pre className="font-body text-ink max-desk:text-[12px] overflow-x-auto px-4 py-3 text-[13px] leading-[1.7] font-normal break-words whitespace-pre-wrap select-all">
            {value}
          </pre>
          <div className="border-hairline flex justify-end border-t px-3 py-2">{button}</div>
        </div>
      ) : (
        <div className="tablet:flex-row tablet:items-center flex flex-col items-start gap-3">
          <code
            className={cn(
              'border-hairline-strong bg-pampas text-ink block max-w-full min-w-0 border px-[14px] py-[10px] break-all select-all',
              emphasis
                ? 'font-display text-name leading-[1.4] font-normal'
                : 'font-body text-[13px] leading-[1.6] font-normal',
            )}
          >
            {value}
          </code>
          {button}
        </div>
      )}
      {state === 'failed' ? (
        <p role="alert" className="text-plum mt-2 text-[12px] leading-[1.6]">
          {failureMessage}
        </p>
      ) : note ? (
        <p className="font-body text-secondary mt-2 text-[12px] leading-[1.6] font-light">{note}</p>
      ) : null}
    </div>
  )
}
